/**
 * Captura un "mapa" de los elementos interactivos VISIBLES en la pantalla actual
 * (botones, links, campos), con una referencia estable (`data-guide-ref`) que el
 * agente puede usar como target exacto y el GuidedTour resaltar.
 *
 * Así el agente guía sobre lo que el cliente REALMENTE ve ahora (incluidas filas
 * dinámicas y estados), sin depender de un vocabulario fijo de data-guide.
 *
 * Excluye el propio modal del asistente (role="dialog") para mapear la página de
 * abajo. Manda solo etiquetas/roles (texto de UI), nunca valores de los campos.
 */
export interface ScreenElement {
  ref: string;
  role: string;
  label: string;
  row?: string; // contexto de fila (ej. "7000000129 ... Error") para desambiguar
}

const INTERACTIVE = 'a, button, [role="button"], [role="link"], input, select, textarea, [data-guide]';

// Busca la etiqueta VISIBLE de un campo: el <label> linkeado por for=id, o el
// primer <label> en el contenedor cercano (patrón típico: <div><label>..</label><select/></div>).
// Así mostramos "Tipo de Identificación" y no el atributo técnico "taxIdType".
function visibleLabel(el: HTMLElement): string {
  const id = el.getAttribute("id");
  if (id) {
    const t = document.querySelector(`label[for="${id}"]`)?.textContent?.trim();
    if (t) return t;
  }
  let node: HTMLElement | null = el.parentElement;
  for (let i = 0; i < 3 && node; i++) {
    const lbl = node.querySelector("label");
    if (lbl?.textContent && !lbl.contains(el)) {
      const t = lbl.textContent.replace(/\s+/g, " ").trim();
      if (t && t.length <= 60) return t;
    }
    node = node.parentElement;
  }
  return "";
}

function labelFor(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "select" || tag === "textarea") {
    const aria = el.getAttribute("aria-label");
    const ph = el.getAttribute("placeholder");
    // Preferimos aria/placeholder/label visible ANTES que el atributo `name` técnico.
    return (aria || ph || visibleLabel(el) || el.getAttribute("name") || "").trim();
  }
  // Botones/links: texto visible, o si es solo-ícono: aria-label / title / data-guide /
  // hint del ícono (clase de lucide/heroicon) para que el agente sepa qué es.
  const text = (el.textContent || "").replace(/\s+/g, " ").trim();
  if (text) return text;
  const aria = (el.getAttribute("aria-label") || el.getAttribute("title") || el.getAttribute("data-guide") || "").trim();
  if (aria) return aria;
  // Hint del ícono: clase tipo "lucide-pencil" / "icon-edit" o el aria-label del <svg>.
  const svg = el.querySelector("svg");
  const svgAria = svg?.getAttribute("aria-label")?.trim();
  if (svgAria) return svgAria;
  const cls = (svg?.getAttribute("class") || "").match(/(?:lucide|icon)-([a-z-]+)/i);
  return cls ? `ícono ${cls[1]}` : "";
}

function rowContext(el: HTMLElement, label: string): string | undefined {
  const row = el.closest<HTMLElement>('tr, [role="row"], li');
  if (!row) return undefined;
  const txt = (row.textContent || "").replace(/\s+/g, " ").trim();
  if (!txt || txt === label) return undefined;
  return txt.slice(0, 90);
}

// Si hay un MODAL abierto (role="dialog"/aria-modal), devolvemos ese contenedor para
// capturar SOLO lo de adentro: así el agente ve únicamente los campos del formulario,
// sin el menú lateral ni el botón "Nuevo X" que queda detrás (eso lo hacía loopear).
function openModalRoot(): ParentNode {
  // Elegimos el dialog VISIBLE que contenga campos/botones de formulario. No filtramos
  // por su bounding box: el wrapper de Headless es position:relative con hijos fixed
  // (tamaño 0), pero igual contiene los inputs. Tomamos el último (el de más arriba).
  const dialogs = Array.from(
    document.querySelectorAll<HTMLElement>('[role="dialog"], [aria-modal="true"]')
  ).filter(
    (el) =>
      !el.closest("[data-support-ui]") &&
      getComputedStyle(el).display !== "none" &&
      el.querySelector("input, select, textarea, button")
  );
  return dialogs.length ? dialogs[dialogs.length - 1] : document;
}

export function captureScreenMap(max = 80): ScreenElement[] {
  // Limpiar refs viejas para que cada captura sea fresca.
  document.querySelectorAll("[data-guide-ref]").forEach((e) => e.removeAttribute("data-guide-ref"));

  const root = openModalRoot();
  const out: ScreenElement[] = [];
  let n = 0;
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(INTERACTIVE))) {
    if (out.length >= max) break;
    if (el.offsetParent === null) continue; // no visible
    // Excluir SOLO la UI del asistente (tooltip, toast, modales propios). Los
    // modales del PORTAL (ej. el form "Nuevo Cliente", que es role=dialog) SÍ se
    // capturan, para poder guiar sus campos.
    if (el.closest("[data-support-ui]")) continue;
    // Excluir el MENÚ LATERAL (nav): son links de navegación que ensucian el mapa
    // y sepultan los campos del form. El agente navega por su lista de pantallas,
    // no necesita estos refs. (No aplica si ya estamos scopeados a un modal.)
    if (root === document && el.closest('nav, aside, [role="navigation"]')) continue;
    const label = labelFor(el);
    const row = rowContext(el, label);
    // Incluimos botones/links de ícono (sin texto) si están en una fila: el agente
    // los targetea por `ref` y los distingue por el contexto de fila. Sin label ni
    // fila no aportan, los salteamos.
    if (!label && !row) continue;
    const ref = `g${++n}`;
    el.setAttribute("data-guide-ref", ref);
    out.push({
      ref,
      role: el.tagName.toLowerCase(),
      label: (label || "(botón de acción)").slice(0, 60),
      ...(row ? { row } : {}),
    });
  }

  // ERRORES DE VALIDACIÓN visibles (texto rojo / role=alert): los agregamos como
  // entradas con rol "error" para que el agente los VEA y guíe a corregir los campos
  // antes de avanzar (ej. "Correo requerido", "País obligatorio B410").
  const seenErr = new Set<string>();
  const errSel = '[role="alert"], [aria-live], [class*="text-red"], [class*="text-danger"], [class*="text-error"]';
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(errSel))) {
    if (seenErr.size >= 12) break;
    if (el.offsetParent === null || el.closest("[data-support-ui]")) continue;
    if (el.querySelector(errSel)) continue; // saltar contenedores, quedarnos con el texto hoja
    const t = (el.textContent || "").replace(/\s+/g, " ").trim();
    if (t.length < 4 || t.length > 140 || seenErr.has(t)) continue;
    seenErr.add(t);
    out.push({ ref: `e${seenErr.size}`, role: "error", label: t.slice(0, 120) });
  }
  return out;
}
